import { NavLink, useNavigate, useLocation } from "react-router-dom";
import {
  ArrowLeft,
  LogOut,
  ChevronDown,
  MoreHorizontal,
  Check,
} from "lucide-react";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@/components/ui/sidebar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/hooks/useAuth";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { adminNavGroups, type AdminNavItem } from "@/config/adminNavigation";
import { Settings } from "lucide-react";

export function AdminSidebar() {
  const { open } = useSidebar();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();

  const { data: profile } = useQuery({
    queryKey: ["profile", user?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("profiles")
        .select("company_name, logo_url")
        .eq("id", user?.id)
        .single();
      if (error) throw error;
      return data;
    },
    enabled: !!user,
    staleTime: Infinity,
    gcTime: Infinity,
  });

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate("/auth");
  };

  const getInitials = (name: string | null | undefined) => {
    if (!name) return "A";
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  const isActive = (url: string) => {
    if (url === "/admins/dashboard") {
      return location.pathname === "/admins" || location.pathname === "/admins/dashboard";
    }
    return location.pathname === url;
  };

  const renderNavItem = (item: AdminNavItem) => {
    const active = isActive(item.url);
    const Icon = item.icon;
    return (
      <SidebarMenuItem key={item.title}>
        <SidebarMenuButton
          asChild
          isActive={active}
          tooltip={!open ? item.title : undefined}
          className={`group transition-colors rounded-lg h-9 ${
            active
              ? "bg-accent text-foreground"
              : "hover:bg-accent text-foreground/80"
          }`}
        >
          <NavLink
            to={item.url}
            className={`flex items-center gap-2.5 w-full ${
              !open ? "justify-center px-0" : "px-2"
            }`}
          >
            <Icon
              className={`h-[18px] w-[18px] shrink-0 ${
                active ? "text-foreground" : "text-muted-foreground"
              }`}
              strokeWidth={1.5}
            />
            {open && (
              <span className="truncate flex-1 text-[13px] font-medium">
                {item.title}
              </span>
            )}
          </NavLink>
        </SidebarMenuButton>
      </SidebarMenuItem>
    );
  };

  return (
    <Sidebar
      collapsible="icon"
      className="border-r border-border/50 bg-sidebar transition-all duration-200"
    >
      {/* Header - Company Selector */}
      <SidebarHeader className="p-2">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              className={`flex items-center gap-2 w-full rounded-lg p-2 hover:bg-accent transition-colors ${
                !open ? "justify-center" : ""
              }`}
            >
              <Avatar className="h-7 w-7 rounded-lg shrink-0">
                {profile?.logo_url ? (
                  <img
                    src={profile.logo_url}
                    alt="Company logo"
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <AvatarFallback className="rounded-lg bg-primary text-primary-foreground text-xs">
                    {getInitials(profile?.company_name)}
                  </AvatarFallback>
                )}
              </Avatar>
              {open && (
                <>
                  <div className="flex-1 text-left min-w-0">
                    <div className="flex items-center gap-1.5">
                      <span className="text-[13px] font-semibold text-foreground truncate">
                        {profile?.company_name || "Admin"}
                      </span>
                      <Badge
                        variant="secondary"
                        className="text-[10px] px-1.5 py-0 h-4 bg-primary/10 text-primary"
                      >
                        Admin
                      </Badge>
                    </div>
                  </div>
                  <ChevronDown className="h-4 w-4 text-muted-foreground shrink-0" />
                </>
              )}
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className="w-56 rounded-lg"
            side="bottom"
            align="start"
            sideOffset={4}
          >
            <div className="px-2 py-1.5">
              <p className="text-[11px] font-medium text-muted-foreground uppercase tracking-wider">
                Admin Panel
              </p>
            </div>
            <DropdownMenuItem
              className="flex items-center gap-2 cursor-pointer"
              onClick={() => navigate("/admins/dashboard")}
            >
              <Check className="h-4 w-4" />
              <span className="text-[13px] font-medium">
                {profile?.company_name || "Admin Dashboard"}
              </span>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              className="flex items-center gap-2 cursor-pointer"
              onClick={() => navigate("/dashboard")}
            >
              <ArrowLeft className="h-4 w-4" />
              <span className="text-[13px]">Back to App</span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarHeader>

      <SidebarContent className="px-2 gap-0">
        {adminNavGroups.map((group, index) => (
          <SidebarGroup key={group.label} className={index === 0 ? "py-0" : "py-0 mt-4"}>
            {open && (
              <SidebarGroupLabel className="px-2 text-[11px] font-medium text-muted-foreground uppercase tracking-wider mb-1">
                {group.label}
              </SidebarGroupLabel>
            )}
            <SidebarGroupContent>
              <SidebarMenu className="gap-0.5">
                {group.items.map(renderNavItem)}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        ))}
      </SidebarContent>

      {/* Footer - User Profile */}
      <SidebarFooter className="p-2 border-t border-border/40">
        <div
          className={`flex items-center gap-2 ${!open ? "justify-center" : ""}`}
        >
          <Avatar className="h-7 w-7 rounded-lg shrink-0">
            {profile?.logo_url ? (
              <img
                src={profile.logo_url}
                alt="Company logo"
                className="h-full w-full object-cover"
              />
            ) : (
              <AvatarFallback className="rounded-lg bg-primary text-primary-foreground text-xs">
                {getInitials(profile?.company_name)}
              </AvatarFallback>
            )}
          </Avatar>
          {open && (
            <>
              <span className="flex-1 text-[13px] text-foreground/80 truncate">
                {user?.email}
              </span>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="p-1 rounded hover:bg-accent transition-colors">
                    <MoreHorizontal className="h-4 w-4 text-muted-foreground" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  className="w-48 rounded-lg"
                  side="top"
                  align="end"
                  sideOffset={8}
                >
                  <DropdownMenuItem
                    onClick={() => navigate("/dashboard")}
                    className="flex items-center gap-2 cursor-pointer"
                  >
                    <ArrowLeft className="h-4 w-4" />
                    <span className="text-[13px]">Back to App</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => navigate("/dashboard/settings")}
                    className="flex items-center gap-2 cursor-pointer"
                  >
                    <Settings className="h-4 w-4" />
                    <span className="text-[13px]">Settings</span>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={handleLogout}
                    className="flex items-center gap-2 cursor-pointer text-destructive focus:text-destructive"
                  >
                    <LogOut className="h-4 w-4" />
                    <span className="text-[13px]">Log out</span>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </>
          )}
        </div>
      </SidebarFooter>
    </Sidebar>
  );
}
